import {Button, Group, Modal, Stack, Text} from '@mantine/core';
import React, {useState} from 'react';

import * as MfaDAO from 'auth/MfaDAO';
import notifications from 'notifications';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';

interface ResetMfaModalProps {
  userName: string;
  userEmail: string;
  userApiId: string;
  onClose: () => void;
  onReset: () => void;
}

export default function ResetMfaModal({
  userName,
  userEmail,
  userApiId,
  onClose,
  onReset,
}: ResetMfaModalProps): JSX.Element {
  const [loading, setLoading] = useState(false);

  async function handleConfirm(): Promise<void> {
    setLoading(true);
    try {
      await MfaDAO.resetMfaEnrollment(userApiId);
      onReset();
      notifications.success('MFA enrollment has been reset.');
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch {
      notifications.error('Failed to reset MFA enrollment.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      title="Confirm multi-factor reset"
      opened={true}
      onClose={onClose}
      size="sm"
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
      withCloseButton={!loading}
    >
      <Stack spacing="md" mb="xl">
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          supportingInfo={`${userName}'s MFA will be disabled.`}
        />
        <Text size="sm">
          {'Any of their open sessions will be logged out, and they will receive an email at '}
          <Text component="span" size="sm" weight={600}>
            {userEmail}
          </Text>
          {' notifying them of this change.'}
        </Text>
        <Text size="sm">They will be able to set up the 2FA on their next login.</Text>
      </Stack>
      <Group position="right" spacing="sm">
        <Button variant="tertiary" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => handleConfirm()} loading={loading}>
          Confirm
        </Button>
      </Group>
    </Modal>
  );
}
